import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface SettingsCardProps {
  /** Leading glyph shown in the tinted badge next to the title. */
  icon: LucideIcon;
  title: string;
  description?: string;
  /** Optional control rendered at the trailing edge of the header (e.g. a status badge). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * One settings group: a card with an icon badge, title and description in the header, a hairline
 * divider, then the group's controls. Shared by every section so the page reads as one consistent set.
 */
export function SettingsCard({
  icon: Icon,
  title,
  description,
  action,
  children,
  className,
}: SettingsCardProps) {
  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex items-start gap-4 p-5">
        <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-muted text-foreground/80 ring-1 ring-foreground/10">
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="font-heading text-base leading-snug font-medium">{title}</h2>
          {description ? (
            <p className="min-h-10 text-sm text-balance text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="border-t border-border/60 p-5">{children}</div>
    </Card>
  );
}
